import { Alert, Button, Loader, Text } from "@mantine/core";
import {
  IconCloudUpload,
  IconFileCheck,
  IconLock,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useCallback, useId, useRef, useState, type DragEvent } from "react";
import styles from "./ResumeParseSection.module.css";

const ACCEPT =
  ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

type Props = {
  file: File | null;
  busy?: boolean;
  error?: string | null;
  hint?: string | null;
  disabled?: boolean;
  lockedMessage?: string | null;
  eyebrow?: string;
  title?: string;
  description?: string;
  busyLabel?: string;
  /** When nested inside a ContentCard, skip outer border/padding to avoid card-in-card. */
  variant?: "card" | "flush";
  onFileChange: (file: File | null) => void;
};

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function ResumeParseSection({
  file,
  busy = false,
  error = null,
  hint = null,
  disabled = false,
  lockedMessage = null,
  eyebrow = "Resume",
  title = "Parse from resume",
  description = "Drop a PDF or DOCX (max 5 MB). We’ll autofill the form — review everything before submitting.",
  busyLabel = "Parsing resume and filling matching fields…",
  variant = "card",
  onFileChange,
}: Props) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const locked = Boolean(lockedMessage);
  const isDisabled = disabled || locked || busy;

  const pickFile = useCallback(() => {
    if (isDisabled) return;
    inputRef.current?.click();
  }, [isDisabled]);

  const takeFiles = useCallback(
    (list: FileList | null) => {
      if (!list?.length || isDisabled) return;
      onFileChange(list[0] ?? null);
    },
    [isDisabled, onFileChange],
  );

  function onDragOver(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (isDisabled) return;
    setDragActive(true);
  }

  function onDragLeave(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (isDisabled) return;
    takeFiles(e.dataTransfer.files);
  }

  return (
    <section
      className={[styles.section, variant === "flush" ? styles.flush : undefined].filter(Boolean).join(" ")}
      aria-labelledby={`${inputId}-title`}
    >
      {variant === "flush" ? null : (
        <div className={styles.head}>
          <Text className={styles.eyebrow} component="span">
            {eyebrow}
          </Text>
          <h3 id={`${inputId}-title`} className={styles.title}>
            {title}
          </h3>
          <p className={styles.hint}>{description}</p>
        </div>
      )}
      {variant === "flush" ? (
        <h3 id={`${inputId}-title`} className={styles.srOnly}>
          {title}
        </h3>
      ) : null}

      {locked ? (
        <p className={styles.lockedNote}>
          <IconLock size="var(--axio-unit-16)" style={{ flexShrink: "var(--axio-number-zero)", marginTop: "var(--axio-unit-2)" }} />
          <span>{lockedMessage}</span>
        </p>
      ) : null}

      <div
        className={styles.dropzone}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        data-active={dragActive}
        data-disabled={isDisabled}
        data-has-file={Boolean(file) && !busy}
        aria-disabled={isDisabled}
        onClick={pickFile}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            pickFile();
          }
        }}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          id={inputId}
          className={styles.hiddenInput}
          type="file"
          accept={ACCEPT}
          disabled={isDisabled}
          onChange={(e) => {
            takeFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className={styles.iconWrap} aria-hidden>
          {file && !busy ? <IconFileCheck size="var(--axio-unit-22)" /> : <IconCloudUpload size="var(--axio-unit-22)" />}
        </div>
        <p className={styles.dropTitle}>
          {busy
            ? "Reading your document…"
            : file
              ? "Document ready — drop another to replace"
              : "Drop file here, or click to browse"}
        </p>
        <p className={styles.dropMeta}>PDF or DOCX · up to 5 MB</p>
        {!busy && !isDisabled ? (
          <div className={styles.actions}>
            <Button
              size="xs"
              variant="light"
              color="violet"
              leftSection={<IconUpload size="var(--axio-unit-14)" />}
              onClick={(e) => {
                e.stopPropagation();
                pickFile();
              }}
            >
              {file ? "Replace file" : "Select file"}
            </Button>
          </div>
        ) : null}
      </div>

      {file ? (
        <div className={styles.fileChip}>
          <div className={styles.fileMeta}>
            <span className={styles.fileName}>{file.name}</span>
            <span className={styles.fileSize}>{formatBytes(file.size)}</span>
          </div>
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            leftSection={<IconTrash size="var(--axio-unit-14)" />}
            disabled={busy}
            onClick={() => onFileChange(null)}
          >
            Remove
          </Button>
        </div>
      ) : null}

      {busy ? (
        <div className={styles.statusRow} role="status">
          <Loader size="sm" color="violet" />
          {busyLabel}
        </div>
      ) : null}

      {error ? (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      ) : null}

      {hint && !error ? (
        <Alert color="violet" variant="light">
          {hint}
        </Alert>
      ) : null}
    </section>
  );
}
